<template>
  <div
    v-if="attachment"
    class="attachment-page-component"
  >
    <template v-if="isAV">
      <media-attachment
        class="media-attachment"
        :attachment="attachment"
        :category="category"
        :poster-attachment="displayAttachment"
        :resource="resource"
      />
    </template>
    <template v-else-if="showDisplayAttachment">
      <img
        :src="displayAttachment.largeThumbnail"
        alt=""
      />
    </template>
    <asset-metadata :asset="attachment" />
    <header-text
      :level="'h2'"
      class="attachment-header"
      :size="'sm'"
    >
      {{ attachment.assetTitle }}
    </header-text>
    <button
      v-if="showOpenButton"
      class="open-button"
      @click="openAttachmentModal"
    >
      Open
    </button>
    <div
      v-if="attachment.mediaType === 'image'"
      class="image-container"
    >
      <img
        class="image"
        :src="attachment.url"
        alt=""
      />
      <button
        class="expand-image-button"
        aria-label="Expand Image"
        @click="openAttachmentModal"
      >
        <icon
          :name="'expand'"
          class="expand-icon"
        />
      </button>
    </div>
    <hlt-text
      v-if="attachment.displayDescription"
      class="attachment-description"
    >
      {{ attachment.displayDescription }}
    </hlt-text>
    <content-source-preview :content-source="contentSource" />
    <divider
      v-if="hasViews"
      :variant="'lightgrey'"
      :spacing="'md'"
    />
    <asset-page-footer-meta
      v-if="hasViews"
      :asset="attachment"
      :total-views="totalViews"
    />
    <divider
      v-if="hasViews"
      :variant="'lightgrey'"
      :spacing="'md'"
    />
    <rating
      :asset="attachment"
      :category="category"
      :rateable-id="attachment.id"
      :rateable-type="'Attachment'"
    />
    <asset-recommendations
      :attachment-ids="recommendedAttachmentIds"
      :page-attachment="attachment"
    />
    <attachment-footer
      v-if="showFooter"
      v-bind="$attrs"
      :show-navigation="showNavigation"
      :attachment="attachment"
      :category="category"
      :next-attachment="nextAttachment"
      :previous-attachment="previousAttachment"
    />
  </div>
</template>

<script>
import AssetMetadata from '@/components/ui/asset-metadata.vue';
import AssetPageFooterMeta from '@/components/ui/asset-page-footer-meta.vue';
import AssetRecommendations from '@/components/assets/recommendations.vue';
import AttachmentFooter from '@/components/attachments/footer.vue';
import ContentSourcePreview from '@/components/content-sources/preview.vue';
import Divider from '@/components/ui/divider.vue';
import HeaderText from '@/components/ui/header-text.vue';
import HltText from '@/components/ui/text.vue';
import Icon from '@/components/singletons/icon.vue';
import MediaAttachment from '@/components/singletons/mediaAttachment.vue';
import Rating from '@/components/singletons/rating.vue';

export default {
  components: {
    AssetMetadata,
    AssetPageFooterMeta,
    AssetRecommendations,
    AttachmentFooter,
    ContentSourcePreview,
    Divider,
    Icon,
    HeaderText,
    MediaAttachment,
    Rating,
    HltText,
  },
  props: {
    attachment: {
      type: Object,
    },
    category: {
      type: Object,
      default: () => ({}),
    },
    contentSource: {
      type: Object,
    },
    displayAttachment: {
      type: Object,
    },
    resource: {
      type: Object,
    },
    nextAttachment: {
      type: Object,
    },
    previousAttachment: {
      type: Object,
    },
    showAssetRecommendations: {
      type: Boolean,
      default: false,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    showNavigation: {
      type: Boolean,
      default: true,
    },
    totalViews: {
      type: Number,
    },
  },
  data() {
    return {
      recommendedAttachmentIds: [],
    };
  },
  computed: {
    isAV() {
      return ['audio', 'video'].includes(this.attachment?.mediaType);
    },
    showDisplayAttachment() {
      return this.displayAttachment && this.attachment?.mediaType === 'biodigital';
    },
    showOpenButton() {
      return this.attachment?.mediaType === 'biodigital';
    },
    hasViews() {
      const n = Number(this.totalViews ?? 0);
      return Number.isFinite(n);
    },
  },
  mounted() {
    this.$watch('attachment', (newAttachment, oldAttachment) => {
      const attachmentHasChanged = newAttachment && newAttachment.id !== oldAttachment?.id;
      if (attachmentHasChanged) {
        const newAttachmentIsAV = ['audio', 'video'].includes(newAttachment.mediaType);
        if (!newAttachmentIsAV) {
          this.$store.dispatch('attachmentStates/send', { attachmentState: { attachmentId: newAttachment.id } });
        }
      }
    }, { immediate: true });
    this.checkRecommendations();
  },
  methods: {
    openAttachmentModal() {
      this.$modal.show('attachment-modal', { attachment: this.attachment });
    },
    checkRecommendations() {
      let seesAssetRecommendations = false;
      if (this.showAssetRecommendations) {
        if (this.$store.getters['user/get']?.previewer) {
          seesAssetRecommendations = true;
        } else {
          seesAssetRecommendations = this.$apptimize.checkExperiments({
            experimentName: 'Asset Page Recommendations',
            bool: 'seesAssetRecommendations',
            defaultVal: false,
            matcher: /Asset Page Recommendations/,
          });
        }
      }
      if (seesAssetRecommendations && this.attachment) {
        this.$store.getters['session/ajax'].get('/recommendations', { params: { attachmentIds: [this.attachment.id] } }).then(({ data }) => {
          this.recommendedAttachmentIds = data.attachmentIds;
          if (data.attachments?.length > 0) {
            this.$store.commit('attachments/setAttachments', data.attachments);
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.attachment-page-component {
  margin: rem-calc(64) auto 0;
  max-width: rem-calc(560);
  width: 100%;

  .attachment-header,
  .attachment-description {
    margin: rem-calc(12) 0;
  }

  .open-button {
    align-items: center;
    background-color: #00B46D;
    border-radius: 4px;
    border: 0;
    color: #ffffff;
    display: flex;
    font-weight: $semi-bold;
    height: rem-calc(40);
    justify-content: center;
    margin: rem-calc(32) 0;
    padding: 0;
    width: 100%;
  }

  .image-container {
    border: 1px solid #E9E9E9;
    margin-bottom: 32px;
    position: relative;
    width: 100%;

    .image {
      object-fit: cover;
      object-position: center;
      width: 100%;
    }

    .expand-image-button {
      position: absolute;
      bottom: 24px;
      right: 24px;
      background: transparent;

      .expand-icon {
        height: 16px;
        width: 16px;
      }
    }
  }

  @include breakpoint(small) {
    margin: 0 auto;

    .media-attachment {
      margin: -2rem -2rem 0;
    }
  }
}
</style>
